import React, { useState } from 'react';
import { createAndStoreSalt, unlockSession } from '../../services/crypto';
import { generateMnemonic, storeRecoveryBlob, mnemonicToQRDataURL, downloadMnemonicTxt } from '../../services/recovery';
import {
  getServerUrl, setServerUrl, syncLogin, syncNow,
  adoptRemoteSalt, verifyKeyCheck, startSyncLoop,
} from '../../services/syncService';
import { SessionKeyStore } from '../../services/keyStore';
import { db } from '../../db/schema';
import { useAuthStore } from '../../store/authStore';
import { CriticalPathGuard } from '../ErrorBoundary';

// ── Schritt 1: Passwort wählen ─────────────────────────────────────────────

function StepPassword(): React.JSX.Element {
  const setStep    = useAuthStore(s => s.setStep);
  const [pw, setPw]     = useState('');
  const [pw2, setPw2]   = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  const validate = (): string | null => {
    if (pw.length < 12) return 'Mindestens 12 Zeichen erforderlich.';
    if (!/[A-Z]/.test(pw)) return 'Mindestens ein Großbuchstabe erforderlich.';
    if (!/[0-9]/.test(pw)) return 'Mindestens eine Zahl erforderlich.';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Mindestens ein Sonderzeichen erforderlich.';
    if (pw !== pw2) return 'Passwörter stimmen nicht überein.';
    return null;
  };

  const handleNext = async (): Promise<void> => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');

    try {
      // Salt erzeugen + speichern
      await createAndStoreSalt();
      // Session entsperren
      await unlockSession(pw);
      // Recovery-Mnemonic erzeugen
      const mnemonic = generateMnemonic();
      await storeRecoveryBlob(pw, mnemonic);
      // Zu Recovery-Anzeige
      (window as unknown as Record<string, unknown>)['__amed_setup_mnemonic'] = mnemonic;
      setStep('setup-recovery');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Master-Passwort festlegen</h2>
        <p className="text-sm text-gray-500 mt-1">
          Dieses Passwort verschlüsselt alle Ihre Daten. Es kann <strong>nicht</strong> zurückgesetzt werden.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label">Passwort</label>
          <input
            type={showPw ? 'text' : 'password'}
            className="input"
            value={pw}
            onChange={e => { setPw(e.target.value); }}
            placeholder="Mindestens 12 Zeichen"
            autoComplete="new-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <div>
          <label className="label">Passwort bestätigen</label>
          <input
            type={showPw ? 'text' : 'password'}
            className="input"
            value={pw2}
            onChange={e => { setPw2(e.target.value); }}
            placeholder="Passwort wiederholen"
            autoComplete="new-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showPw}
            onChange={e => { setShowPw(e.target.checked); }}
            className="rounded border-gray-300 text-blue-600" />
          👁 Passwort anzeigen
        </label>
      </div>

      {/* Stärke-Indikatoren */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {[
          ['≥ 12 Zeichen', pw.length >= 12],
          ['Großbuchstabe', /[A-Z]/.test(pw)],
          ['Zahl', /[0-9]/.test(pw)],
          ['Sonderzeichen', /[^A-Za-z0-9]/.test(pw)],
        ].map(([label, ok]) => (
          <span key={String(label)} className={`flex items-center gap-1 ${ok ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{ok ? '✓' : '○'}</span> {String(label)}
          </span>
        ))}
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}

      <button onClick={() => { void handleNext(); }} className="btn-primary w-full justify-center">
        Weiter zur Recovery-Wortliste →
      </button>
    </div>
  );
}

// ── Alternative: Zugang von anderem Gerät wiederherstellen ─────────────────

function StepRestore({ onBack }: { onBack: () => void }): React.JSX.Element {
  const setStep = useAuthStore(s => s.setStep);
  const [server,   setServer]   = useState('');
  const [email,    setEmail]    = useState('');
  const [syncPw,   setSyncPw]   = useState('');
  const [masterPw, setMasterPw] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const [progress, setProgress] = useState('');

  React.useEffect(() => { void getServerUrl().then(setServer); }, []);

  const handleRestore = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      setProgress('Verbinde mit Server…');
      await setServerUrl(server);
      await syncLogin(email, syncPw);

      setProgress('Übernehme Verschlüsselungs-Parameter…');
      await adoptRemoteSalt();

      setProgress('Leite Schlüssel ab (PBKDF2 600k)…');
      await unlockSession(masterPw);

      setProgress('Lade Daten vom Server…');
      /* Boot-Seeds (Default-Mandanten) aus der Outbox werfen — sonst würde
       * dieses frische Gerät die echten Server-Daten überschreiben. */
      await db.sync_outbox.clear();
      await syncNow();

      setProgress('Prüfe Master-Passwort…');
      const ok = await verifyKeyCheck();
      if (!ok) {
        SessionKeyStore.wipe();
        throw new Error(
          'Master-Passwort stimmt nicht mit dem Haupt-Gerät überein. ' +
          'Bitte exakt dasselbe Passwort wie dort verwenden.',
        );
      }

      setProgress('Erzeuge Recovery-Wortliste für dieses Gerät…');
      const mnemonic = generateMnemonic();
      await storeRecoveryBlob(masterPw, mnemonic);
      (window as unknown as Record<string, unknown>)['__amed_setup_mnemonic'] = mnemonic;
      startSyncLoop();
      setStep('setup-recovery');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Zugang wiederherstellen</h2>
        <p className="text-sm text-gray-500 mt-1">
          Übernimmt Ihre Daten von der zentralen Datenbank auf dieses Gerät.
          Sie brauchen Ihr Sync-Konto und Ihr Master-Passwort vom Haupt-Gerät.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label">Server-URL</label>
          <input type="url" className="input" value={server}
            onChange={e => { setServer(e.target.value); }}
            placeholder="https://amed.dr-karrer.com" />
        </div>
        <div>
          <label className="label">E-Mail (Sync-Konto)</label>
          <input type="email" className="input" value={email}
            onChange={e => { setEmail(e.target.value.trim()); }}
            placeholder="mail@dr-karrer.com" autoComplete="username"
            autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </div>
        <div>
          <label className="label">Sync-Passwort</label>
          <input type={showPw ? 'text' : 'password'} className="input" value={syncPw}
            onChange={e => { setSyncPw(e.target.value); }}
            autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </div>
        <div>
          <label className="label">Master-Passwort (wie am Haupt-Gerät)</label>
          <input type={showPw ? 'text' : 'password'} className="input" value={masterPw}
            onChange={e => { setMasterPw(e.target.value); }}
            autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showPw}
            onChange={e => { setShowPw(e.target.checked); }}
            className="rounded border-gray-300 text-blue-600" />
          👁 Passwörter anzeigen
        </label>
      </div>

      {progress !== '' && (
        <p className="text-sm text-blue-700 bg-blue-50 rounded p-2 flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin inline-block" />
          {progress}
        </p>
      )}
      {error !== '' && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}

      <button onClick={() => { void handleRestore(); }} disabled={busy}
        className="btn-primary w-full justify-center disabled:opacity-50">
        {busy ? 'Stelle wieder her…' : 'Daten übernehmen →'}
      </button>
      <button onClick={onBack} disabled={busy}
        className="text-sm text-gray-500 hover:text-gray-700 w-full text-center">
        ← Zurück
      </button>
    </div>
  );
}

// ── Schritt 0: Modus wählen ────────────────────────────────────────────────

function StepChoose({ onNew, onRestore }: {
  onNew: () => void;
  onRestore: () => void;
}): React.JSX.Element {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Wie möchten Sie starten?</h2>
        <p className="text-sm text-gray-500 mt-1">
          Auf diesem Gerät wurde die AMed App noch nicht eingerichtet.
        </p>
      </div>
      <button onClick={onNew}
        className="w-full text-left border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
        <p className="font-semibold text-gray-900">🆕 Neue Einrichtung</p>
        <p className="text-sm text-gray-500 mt-0.5">
          Erster Start — neues Master-Passwort und leere Datenbank anlegen.
        </p>
      </button>
      <button onClick={onRestore}
        className="w-full text-left border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
        <p className="font-semibold text-gray-900">🔄 Zugang wiederherstellen</p>
        <p className="text-sm text-gray-500 mt-0.5">
          Zweitgerät — Daten aus Ihrer zentralen Datenbank übernehmen
          (Sync-Konto + Master-Passwort nötig).
        </p>
      </button>
    </div>
  );
}

// ── Schritt 2: Recovery-Wortliste anzeigen ─────────────────────────────────

function StepRecovery(): React.JSX.Element {
  const setStep         = useAuthStore(s => s.setStep);
  const setSetupComplete = useAuthStore(s => s.setSetupComplete);
  const [qrUrl, setQrUrl]     = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const mnemonic = String((window as unknown as Record<string, unknown>)['__amed_setup_mnemonic'] ?? '');
  const words    = mnemonic.split(' ');

  const handleShowQR = async (): Promise<void> => {
    const url = await mnemonicToQRDataURL(mnemonic);
    setQrUrl(url);
  };

  const handleFinish = (): void => {
    // Mnemonic aus Window-Objekt entfernen
    delete (window as unknown as Record<string, unknown>)['__amed_setup_mnemonic'];
    setSetupComplete(true);
    setStep('unlocked');
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Recovery-Wortliste sichern</h2>
        <p className="text-sm text-red-600 font-medium mt-1">
          ⚠ Diese 24 Wörter sind die einzige Möglichkeit, Ihr Passwort wiederherzustellen!
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2 bg-gray-50 rounded-xl p-4 border border-gray-200">
        {words.map((word, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="text-gray-400 w-5 text-right text-xs">{i + 1}.</span>
            <span className="font-mono font-medium text-gray-900">{word}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={() => { downloadMnemonicTxt(mnemonic); }} className="btn-secondary flex-1 justify-center text-xs">
          Als TXT speichern
        </button>
        <button onClick={() => { void handleShowQR(); }} className="btn-secondary flex-1 justify-center text-xs">
          QR-Code anzeigen
        </button>
      </div>

      {qrUrl && (
        <div className="flex flex-col items-center gap-2">
          <img src={qrUrl} alt="Recovery QR-Code" className="w-40 h-40 border rounded-lg" />
          <p className="text-xs text-gray-500">QR-Code ausdrucken und sicher aufbewahren</p>
        </div>
      )}

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={e => { setConfirmed(e.target.checked); }}
          className="mt-0.5"
        />
        <span className="text-sm text-gray-700">
          Ich habe die Wortliste sicher verwahrt und verstehe, dass bei Verlust
          alle Daten unwiederbringlich verloren sind.
        </span>
      </label>

      <button
        onClick={handleFinish}
        disabled={!confirmed}
        className="btn-primary w-full justify-center"
      >
        Setup abschließen → App starten
      </button>
    </div>
  );
}

// ── Setup-Wizard-Shell ─────────────────────────────────────────────────────

export function SetupWizard(): React.JSX.Element {
  const step = useAuthStore(s => s.step);
  const [mode, setMode] = useState<'choose' | 'new' | 'restore'>('choose');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="card max-w-lg w-full">
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-blue-700 flex items-center justify-center">
            <span className="text-white font-bold">A</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900">AMed App — Ersteinrichtung</p>
            <p className="text-xs text-gray-400">Dr. med. Klaus Karrer, MSc.</p>
          </div>
          <div className="ml-auto flex gap-1.5">
            {(['setup-new', 'setup-recovery'] as const).map((s, i) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full ${step === s ? 'bg-blue-700' : i < (['setup-new', 'setup-recovery'] as const).indexOf(step as 'setup-new' | 'setup-recovery') ? 'bg-green-500' : 'bg-gray-200'}`}
              />
            ))}
          </div>
        </div>

        <CriticalPathGuard guardName="Setup-Wizard">
          {step === 'setup-recovery'
            ? <StepRecovery />
            : mode === 'choose'
              ? <StepChoose onNew={() => { setMode('new'); }} onRestore={() => { setMode('restore'); }} />
              : mode === 'restore'
                ? <StepRestore onBack={() => { setMode('choose'); }} />
                : <StepPassword />
          }
        </CriticalPathGuard>
      </div>
    </div>
  );
}
